import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Badge, Spinner } from 'react-bootstrap';
import { db } from '../../firebase';
import {
	collection,
	doc,
	addDoc,
	query,
	where,
	getDocs,
	updateDoc,
	runTransaction,
	Timestamp,
} from 'firebase/firestore';
import { assignStudentToSlot, fetchRegistrationRequests } from '../../utils/registrationService';
import {
	findRequestMatchesForStudent,
	pickDefaultMatch,
	matchKey,
} from '../../utils/registrationRequestMatch';
import { formatTime12Hour, getDayName } from '../../utils/schedulingUtils';

/**
 * ASSIGNING IS A CONTESTED WRITE, and this modal used to treat it as a private
 * one (fixed 2026-08-06, card wW6lKRdSTxE4uvp1Vld3).
 *
 * Two people place students at the same time — Grace working carryover calls,
 * Anthony working new registrations — and both were reading the open pool and
 * writing with a plain `updateDoc`. Two writes on one slot both SUCCEEDED and
 * the second silently replaced the first: a student lost their time, nothing
 * errored, and nothing recorded that it had happened.
 *
 * Regular lessons now go through the `assignStudentToSlot` callable — the same
 * first-wins transaction the Registration tab uses — so a lost race comes back
 * as an error a human reads instead of a booking a human loses. It also stamps
 * the provenance fields (assignedAt / assignedBy / assignedVia) that a
 * client-side write never wrote.
 *
 * TWO CONSEQUENCES, both deliberate:
 *  - The callable is admin/owner-only, so assigning from this modal now matches
 *    the Registration tab's gate. It NARROWS what an employee-with-viewSchedule
 *    could previously do.
 *  - The callable knows nothing about makeups. That branch keeps a client write
 *    — but inside a transaction that re-checks the slot is still open, so it
 *    gets the same guarantee by a different road. Extending the callable to
 *    cover makeups is a later step on the card, not a silent widening here.
 *
 * REQUEST LINKING (step 2, 2026-08-06). Placing a child here when their family
 * DID submit a `/register` request used to leave that child listed as awaiting
 * assignment in the Registration tab while their slot vanished from the open
 * pool — one job, two surfaces, disagreeing. The callable has always accepted
 * `requestId`/`childId` and advanced the request; this modal simply never had
 * one in hand. It now looks for the family's pending request and offers it.
 *
 * OFFERS, not resolves. A request child carries a parent-typed `childName` and
 * no student id until an assign writes one, so the match is a name guess —
 * see `registrationRequestMatch.js` for why only one unambiguous shape is ever
 * pre-selected. Linking is skippable and the assign never depends on it: a
 * failed or empty request lookup must still leave a working modal, because
 * placing the student is the job and closing the ghost row is the courtesy.
 * Makeups never link — a one-off makeup has no registration request behind it.
 */
const SLOT_TAKEN = 'SLOT_TAKEN';

// ORDERED FOR DISPLAY, NOT INDEXED BY dayOfWeek. This list is Monday-first so
// the dropdown reads like a teaching week, which puts Sunday at index 6 while
// its `value` is 0 — so `DAYS_OF_WEEK[slot.dayOfWeek]` is off by one for every
// day and turns Sunday into Monday. It did exactly that in the selected-slot
// badge until 2026-08-06. Group by `.value` (as the optgroups below do), and
// use `getDayName` (Sunday-first, matching Date.getDay) to name a day.
const DAYS_OF_WEEK = [
	{ value: 1, label: 'Monday' },
	{ value: 2, label: 'Tuesday' },
	{ value: 3, label: 'Wednesday' },
	{ value: 4, label: 'Thursday' },
	{ value: 5, label: 'Friday' },
	{ value: 6, label: 'Saturday' },
	{ value: 0, label: 'Sunday' }
];

const INSTRUCTORS = ['Grace', 'Anthony', 'Annelise'];
const LOCATIONS = ['Lakeville', 'Headline'];

const AssignTimeSlot = ({ show, onHide, student, onSuccess }) => {
	const [lessonType, setLessonType] = useState('regular'); // 'regular' or 'makeup'
	const [availableSlots, setAvailableSlots] = useState([]);
	const [selectedSlotId, setSelectedSlotId] = useState('');
	const [selectedSlot, setSelectedSlot] = useState(null);
	const [specificDate, setSpecificDate] = useState('');
	const [notes, setNotes] = useState('');
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [requestMatches, setRequestMatches] = useState([]);
	const [linkedKey, setLinkedKey] = useState('');

	// Fetch available slots when modal opens
	useEffect(() => {
		if (show) {
			resetForm();
			fetchAvailableSlots();
		}
	}, [show, lessonType]);

	// Pending-request lookup is separate from the slot pool on purpose: it does
	// not depend on lessonType (makeups just don't render it), and it must never
	// gate the modal — an admin whose request read fails can still assign.
	useEffect(() => {
		if (!show || !student?.id) return;
		let cancelled = false;
		(async () => {
			try {
				const requests = await fetchRegistrationRequests();
				if (cancelled) return;
				const matches = findRequestMatchesForStudent({ requests, student });
				setRequestMatches(matches);
				setLinkedKey(matchKey(pickDefaultMatch(matches)));
			} catch (err) {
				// Non-fatal by design. Degrade to "no request to link" rather than
				// blocking the placement this modal exists to perform.
				console.error('Error loading registration requests:', err);
				if (!cancelled) {
					setRequestMatches([]);
					setLinkedKey('');
				}
			}
		})();
		return () => {
			cancelled = true;
		};
		// Keyed on the student's IDENTITY, not the object: the parent re-creates
		// `student` on every render, and depending on it would re-query
		// registrationRequests continuously while the modal is open.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, student?.id]);

	const resetForm = () => {
		setSelectedSlotId('');
		setSelectedSlot(null);
		setSpecificDate('');
		setNotes('');
		setError('');
	};

	const linkedMatch =
		lessonType === 'regular' ? requestMatches.find((m) => matchKey(m) === linkedKey) || null : null;

	const fetchAvailableSlots = async () => {
		setLoading(true);
		try {
			const timeSlotsRef = collection(db, 'timeSlots');
			const q = query(
				timeSlotsRef,
				where('status', '==', 'open'),
				where('isRecurring', '==', lessonType === 'regular')
			);

			const snapshot = await getDocs(q);
			const slots = snapshot.docs.map(doc => ({
				id: doc.id,
				ref: doc.ref,
				...doc.data()
			}));

			// Sort by day, then by time
			slots.sort((a, b) => {
				if (a.dayOfWeek !== b.dayOfWeek) {
					return a.dayOfWeek - b.dayOfWeek;
				}
				return a.startTime.localeCompare(b.startTime);
			});

			setAvailableSlots(slots);
		} catch (err) {
			console.error('Error fetching available slots:', err);
			setError('Error loading available time slots');
		} finally {
			setLoading(false);
		}
	};

	const handleSlotSelection = (slotId) => {
		setSelectedSlotId(slotId);
		const slot = availableSlots.find(s => s.id === slotId);
		setSelectedSlot(slot);
	};

	const handleAssignSlot = async () => {
		if (!selectedSlot) {
			setError('Please select a time slot');
			return;
		}

		if (lessonType === 'makeup' && !specificDate) {
			setError('Please select a specific date for makeup lessons');
			return;
		}

		setSaving(true);
		setError('');

		const trimmedNotes = notes.trim();

		try {
			if (lessonType === 'regular') {
				// The guarded path. requestId/childId ride along ONLY when a human
				// left a request selected — the callable treats them as optional and
				// advances the request to worked/placed when they are present.
				await assignStudentToSlot({
					slotId: selectedSlot.id,
					studentId: student.id,
					...(linkedMatch
						? { requestId: linkedMatch.requestId, childId: linkedMatch.childId }
						: {}),
				});

				// The callable writes identity + provenance but not `notes`, which
				// only this modal collects. Written after, and safe to write plainly:
				// the race is over and this slot is ours.
				if (trimmedNotes) {
					await updateDoc(selectedSlot.ref, {
						notes: trimmedNotes,
						updatedAt: Timestamp.now(),
					});
				}
			} else {
				// Makeups have no callable. Same guarantee, client side: re-read the
				// slot INSIDE a transaction and refuse if it is no longer open. A
				// read-then-write outside one would be the very race this fixes.
				await runTransaction(db, async (tx) => {
					const ref = doc(db, 'timeSlots', selectedSlot.id);
					const snap = await tx.get(ref);
					if (!snap.exists()) throw new Error(SLOT_TAKEN);
					const current = snap.data() || {};
					if (current.status !== 'open' || current.studentId) throw new Error(SLOT_TAKEN);
					tx.update(ref, {
						studentId: student.id,
						studentName: student.fullName || `${student.firstName} ${student.lastName}`,
						status: 'assigned',
						isMakeup: true,
						specificDate,
						notes: trimmedNotes,
						assignedAt: Timestamp.now(),
						assignedVia: 'student-profile-makeup',
						updatedAt: Timestamp.now(),
					});
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			onHide();
		} catch (err) {
			console.error('Error assigning time slot:', err);
			// A lost race is not a failure to report as "try again" — the slot is
			// gone and retrying the same choice would lose again. Say what happened
			// and refresh the pool so the stale option disappears from the list.
			const lostRace =
				err?.message === SLOT_TAKEN ||
				err?.code === 'failed-precondition' ||
				err?.code === 'functions/failed-precondition';
			if (lostRace) {
				setError('That slot was just filled — pick another time.');
				await fetchAvailableSlots();
				setSelectedSlotId('');
				setSelectedSlot(null);
			} else if (err?.code === 'permission-denied' || err?.code === 'functions/permission-denied') {
				setError('Assigning a lesson time is admin-only. Ask Anthony or Grace to place this student.');
			} else if (
				linkedMatch &&
				(err?.code === 'not-found' || err?.code === 'functions/not-found')
			) {
				// The linked request moved under us (re-worked elsewhere, archived).
				// Nothing was written — the whole assign is one transaction — so the
				// recovery is to drop the link, not to retry the same call.
				setError(
					'That registration request has changed. Choose "Don\'t link a registration request" and assign again.'
				);
			} else {
				setError('Error assigning time slot. Please try again.');
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal show={show} onHide={onHide} size="lg">
			<Modal.Header closeButton style={{ backgroundColor: '#384971', color: '#fff' }}>
				<Modal.Title>
					<i className="fa-sharp fa-solid fa-calendar-plus me-2"></i>
					Assign Time Slot - {student?.fullName || student?.firstName}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{error && <Alert variant="danger">{error}</Alert>}

				{/* Lesson Type Selection */}
				<Form.Group className="mb-4">
					<Form.Label className="fw-bold">Lesson Type *</Form.Label>
					<div className="d-flex gap-3">
						<Form.Check
							type="radio"
							id="type-regular"
							label={
								<span>
									<i className="fa-sharp fa-solid fa-repeat me-2"></i>
									Regular (Recurring Weekly)
								</span>
							}
							checked={lessonType === 'regular'}
							onChange={() => setLessonType('regular')}
						/>
						<Form.Check
							type="radio"
							id="type-makeup"
							label={
								<span>
									<i className="fa-sharp fa-solid fa-calendar-day me-2"></i>
									Make-up (One-time)
								</span>
							}
							checked={lessonType === 'makeup'}
							onChange={() => setLessonType('makeup')}
						/>
					</div>
					<Form.Text className="text-muted">
						{lessonType === 'regular'
							? 'Regular lessons repeat every week on the same day and time'
							: 'Make-up lessons are scheduled for a specific date only'}
					</Form.Text>
				</Form.Group>

				{loading ? (
					<div className="text-center py-4">
						<Spinner animation="border" variant="primary" />
						<p className="mt-2">Loading available time slots...</p>
					</div>
				) : availableSlots.length === 0 ? (
					<Alert variant="warning">
						<i className="fa-sharp fa-solid fa-exclamation-triangle me-2"></i>
						No available {lessonType} time slots found. Please create open slots first or choose a different lesson type.
					</Alert>
				) : (
					<>
						{/* Time Slot Selection */}
						<Form.Group className="mb-3" controlId="assign-slot">
							<Form.Label className="fw-bold">Select Available Time Slot *</Form.Label>
							<Form.Select
								value={selectedSlotId}
								onChange={(e) => handleSlotSelection(e.target.value)}
								size="lg"
							>
								<option value="">Choose a time slot...</option>
								{DAYS_OF_WEEK.map(day => {
									const daySlots = availableSlots.filter(s => s.dayOfWeek === day.value);
									if (daySlots.length === 0) return null;

									return (
										<optgroup key={day.value} label={`${day.label} (${daySlots.length} available)`}>
											{daySlots.map(slot => (
												<option key={slot.id} value={slot.id}>
													{formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}{slot.instructor ? ` • ${slot.instructor}` : ''} • {slot.location}
												</option>
											))}
										</optgroup>
									);
								})}
							</Form.Select>
						</Form.Group>

						{/* Selected Slot Details */}
						{selectedSlot && (
							<Alert variant="info">
								<strong>Selected Time Slot:</strong>
								<div className="mt-2">
									<Badge bg="primary" className="me-2">{getDayName(selectedSlot.dayOfWeek)}</Badge>
									<Badge bg="secondary" className="me-2">{formatTime12Hour(selectedSlot.startTime)} - {formatTime12Hour(selectedSlot.endTime)}</Badge>
									{selectedSlot.instructor && <Badge bg="info" className="me-2">{selectedSlot.instructor}</Badge>}
									<Badge bg="dark">{selectedSlot.location}</Badge>
								</div>
								<div className="mt-2">
									<small className="text-muted">Duration: {selectedSlot.duration} minutes</small>
								</div>
							</Alert>
						)}

						{/* Specific Date for Makeup */}
						{lessonType === 'makeup' && selectedSlot && (
							<Form.Group className="mb-3" controlId="assign-specific-date">
								<Form.Label className="fw-bold">Specific Date *</Form.Label>
								<Form.Control
									type="date"
									value={specificDate}
									onChange={(e) => setSpecificDate(e.target.value)}
									min={new Date().toISOString().split('T')[0]}
								/>
								<Form.Text className="text-muted">
									Choose the specific date for this one-time makeup lesson
								</Form.Text>
							</Form.Group>
						)}

						{/* Registration request link — offered, never assumed */}
						{lessonType === 'regular' && requestMatches.length > 0 && (
							<Form.Group className="mb-3">
								<Form.Label className="fw-bold">
									<i className="fa-sharp fa-solid fa-link me-2"></i>
									Registration request
								</Form.Label>
								<Form.Text className="d-block text-muted mb-2">
									{requestMatches.length === 1
										? 'This family submitted a registration request. Linking it marks the child placed so they stop showing as unassigned.'
										: 'More than one pending request matches this name — pick the right one, or leave it unlinked.'}
								</Form.Text>
								{requestMatches.map((match) => {
									const key = matchKey(match);
									return (
										<Form.Check
											type="radio"
											name="assign-request-link"
											key={key}
											id={`assign-request-${key}`}
											value={key}
											checked={linkedKey === key}
											onChange={() => setLinkedKey(key)}
											label={
												<span>
													{match.childName}
													{match.parentName ? ` — parent: ${match.parentName}` : ''}
													{match.createdAtMs
														? ` — submitted ${new Date(match.createdAtMs).toLocaleDateString()}`
														: ''}
													{match.confidence === 'partial' && (
														<Badge bg="warning" text="dark" className="ms-2">
															first name only
														</Badge>
													)}
												</span>
											}
										/>
									);
								})}
								<Form.Check
									type="radio"
									name="assign-request-link"
									id="assign-request-none"
									value=""
									checked={linkedKey === ''}
									onChange={() => setLinkedKey('')}
									label="Don't link a registration request"
								/>
							</Form.Group>
						)}

						{/* Notes */}
						<Form.Group className="mb-3" controlId="assign-notes">
							<Form.Label className="fw-bold">Notes (Optional)</Form.Label>
							<Form.Control
								as="textarea"
								rows={2}
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Add any special notes about this lesson..."
							/>
						</Form.Group>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onHide} disabled={saving}>
					Cancel
				</Button>
				<Button
					variant="success"
					onClick={handleAssignSlot}
					disabled={saving || !selectedSlot || (lessonType === 'makeup' && !specificDate)}
				>
					{saving ? (
						<>
							<Spinner animation="border" size="sm" className="me-2" />
							Assigning...
						</>
					) : (
						<>
							<i className="fa-sharp fa-solid fa-check me-2"></i>
							Assign Student to This Slot
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default AssignTimeSlot;
